import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, TextField, IconButton, Tooltip } from "@mui/material";
import CheckingNotifications from "assets/CheckingNotifications.png";
import { ImagesSquare, PaperPlaneTilt } from "@phosphor-icons/react";
import UploadAttachmentDialog from "./UploadAttachmentDialog";
import ChimeMessageList from "./ChimeMessageList";
import { prepareChimeAttachment } from "./util";

/**
 * ChimeChat component: fills its parent vertically, displays chat history and new messages,
 * allows sending persistent "CH" messages with metadata including avatarUrl and fullName.
 *
 * @param {Object} props
 * @param {import('../../video/messaging/MessagingSession').default} props.messagingSession
 * @param {Object} props.user - current user, with `id`, `first_name`, `last_name`, and optional `image_url`
 */
const ChimeChat = ({
  messagingSession,
  user,
  clientId,
  designatedCaregiverCognitoId,
  chatOpen,
}) => {
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [input, setInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);

  const recipients = [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (chatOpen && messagingSession) {
      messagingSession.markMessagesAsRead();
    }
  }, [chatOpen]);

  const buildMessageFromChime = async (msg) => {
    const metadata = msg.Metadata ? JSON.parse(msg.Metadata) : undefined;
    if (metadata?.attachmentId) {
      await prepareChimeAttachment(metadata, designatedCaregiverCognitoId);
    }

    return {
      id: msg.MessageId,
      content: msg.Content,
      metadata,
      isOutbound: msg.Sender?.Name === user.id,
    };
  };

  useEffect(() => {
    if (!messagingSession) return;
    let isMounted = true;

    const handleIncoming = async (message) => {
      if (message.ContentType !== "CH") return;
      const enrichedMessage = await buildMessageFromChime(message);
      setMessages((prev) => [...prev, enrichedMessage]);
      messagingSession.markMessagesAsRead();
    };

    async function initChat() {
      setMessagesLoading(true);
      const history = await messagingSession.loadChatHistory();
      if (!isMounted) return;
      const chatHistory = [];

      for (const msg of history) {
        const enrichedMessage = await buildMessageFromChime(msg);
        chatHistory.push(enrichedMessage);
      }
      setMessages(chatHistory);
      setMessagesLoading(false);
      messagingSession.addMessageListener(handleIncoming, "CH");
      messagingSession.markMessagesAsRead();
    }

    initChat();
    return () => {
      isMounted = false;
      messagingSession.removeMessageListener(handleIncoming, "CH");
    };
  }, [messagingSession, user.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (attachmentId = null) => {
    const text = attachmentId ? "attachment" : input.trim();
    if ((!text && !attachmentId) || !messagingSession) return;
    const metadata = {
      sentAt: new Date().toISOString(),
      avatarUrl: user.image_url || null,
      fullName: `${user.first_name} ${user.last_name}`,
      attachmentId,
    };
    await messagingSession.sendPersistentChatMessage(text, recipients, metadata);
    setInput("");
  };

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            p: 7,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {messages.length === 0 && !messagesLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginTop: "auto",
              }}
            >
              <Box
                component="img"
                src={CheckingNotifications}
                alt="No messages"
                sx={{ display: "block", width: 160, height: "auto" }}
              />
              <Typography variant="h3" fontFamily="TestTiemposText">
                Say hello!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Chat with people on this call.
              </Typography>
            </Box>
          ) : (
            <ChimeMessageList messages={messages} messagesEndRef={messagesEndRef} />
          )}
        </Box>
        <Box sx={{ p: 7, display: "flex", alignItems: "center", gap: 3 }}>
          <Tooltip title="Add attachment" placement="top">
            <IconButton
              onClick={() => setDialogOpen(true)}
              aria-label="upload"
              sx={{ "& svg": { height: 20, width: 20 } }}
            >
              <ImagesSquare size={20} color="rgba(108, 116, 137, 1)" weight="duotone" />
            </IconButton>
          </Tooltip>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Write a message..."
            disabled={uploading}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <Tooltip title="Send message" placement="top">
            <IconButton
              onClick={() => sendMessage()}
              aria-label="upload"
              disabled={!input.trim() || uploading}
              sx={{ "& svg": { height: 20, width: 20 } }}
            >
              <PaperPlaneTilt size={20} color="rgba(108, 116, 137, 1)" weight="duotone" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <UploadAttachmentDialog
        open={dialogOpen}
        handleClose={() => setDialogOpen(false)}
        uploading={uploading}
        setUploading={setUploading}
        cognitoId={designatedCaregiverCognitoId}
        sendMessage={sendMessage}
        clientId={clientId}
        user={user}
      />
    </>
  );
};

export default ChimeChat;
